import { downsampleToWav, encodeMp3, getAudioInfo } from "@plugins/audio-recorder/WebAudioUtils";

export default class AudioRecorder {
  constructor(options) {
    this.onInitSuccessCallBack = options.onInitSuccessCallBack || null;
    this.onInitFailCallBack = options.onInitFailCallBack || null;
    this.onStartCallBack = options.onStartCallBack || null;
    this.onStopCallBack = options.onStopCallBack || null;
    this.onRecordingError = options.onRecordingError || null;
    // default audio time limit is 10 mins, so default maxSliceOfDuration is 600000
    this.maxSliceOfDuration = options.maxSliceOfDuration || 600000;
    this.duration = 0;
    this.mediaRecorder = null;
    this.chunks = [];
    this.audioInfo = getAudioInfo();
    this.endDuration = 0;
    this.reachedMaxChunk = options.reachedMaxChunk || null;
    this.forceStop = false;

    if (!navigator || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      if (this.onInitFailCallBack) {
        this.onInitFailCallBack({
          code: 1, //"Browser not support as getUserMedia is not implemented in this browser "
        });
      }
    } else if (!window.MediaRecorder) {
      if (this.onInitFailCallBack) {
        this.onInitFailCallBack({
          code: 1, //"Browser not support as MediaRecorder is not implemented in this browser "
        });
      }
    } else {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then(this.handleSuccess.bind(this), this.handleError.bind(this));
    }
  }
  handleSuccess(stream) {
    let mimeType = "";
    if (window.MediaRecorder.isTypeSupported("audio/webm")) {
      mimeType = "audio/webm";
    } else if (window.MediaRecorder.isTypeSupported("audio/ogg")) {
      mimeType = "audio/ogg";
    } else if (window.MediaRecorder.isTypeSupported("audio/mp4")) {
      mimeType = "audio/mp4";
    }
    this.mediaRecorder = new window.MediaRecorder(stream, {
      audioBitsPerSecond: "128000",
      mimeType: mimeType,
    });

    this.mediaRecorder.onstop = this.mediaRecorderOnStop.bind(this);

    this.mediaRecorder.ondataavailable = function (event) {
      this.chunks.push(event.data);
      this.stopRecording();
    }.bind(this);

    if (this.onInitSuccessCallBack) {
      this.onInitSuccessCallBack();
    }
  }
  handleError(error) {
    let errorCode;
    const errorName = error.name;
    if (errorName === "PermissionDeniedError" || errorName === "NotAllowedError") {
      errorCode = 2;
    } else if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
      errorCode = 3;
    } else if (errorName === "NotReadableError") {
      errorCode = 4;
    }

    if (this.onInitFailCallBack) {
      this.onInitFailCallBack({
        code: errorCode, //"Permission denied or no microphone detected"
      });
    }
  }
  startRecording() {
    if (this.mediaRecorder) {
      // set time slice based on question's max duration
      this.mediaRecorder.start(this.maxSliceOfDuration);
      this.startTime = Date.now();
    }

    if (this.onStartCallBack) {
      this.onStartCallBack();
    }
  }
  stopRecording() {
    this.duration = Date.now() - this.startTime;

    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }
  }
  mediaRecorderOnStop() {
    if (this.forceStop) {
      this.stopTrackAndResetDuration();
      // cater when user tries to record recording when already recording one
      this.forceStop = false;
      return;
    }

    const onStopCallBack = this.onStopCallBack;
    if (!onStopCallBack) return;

    const blob = new Blob(this.chunks);
    const dur = Math.floor(this.duration * 0.001);
    const durStr = this.convertToReadableTime(dur);
    this.endDuration = dur;
    this.chunks = [];

    if (this.maxSliceOfDuration * 0.001 <= dur) {
      this.reachedMaxChunk();
    }

    if (blob.size === 0 && this.onRecordingError) {
      this.onRecordingError();
      this.stopTrackAndResetDuration();
      return;
    }

    const fileExt = this.audioInfo.ext;
    const file = new File([blob], `recording.${fileExt}`, {
      type: blob.type,
    });

    downsampleToWav(file, function (buffer) {
      const mp3Buffer = encodeMp3(buffer);
      const mp3blob = new Blob(mp3Buffer, { type: "audio/mp3" });

      onStopCallBack({
        fileType: "audio/mp3",
        fileName: "recording.mp3",
        audioUrl: window.URL.createObjectURL(mp3blob),
        blob: mp3blob,
        duration: dur,
        durationStr: durStr,
      });
    });
    this.stopTrack();
  }
  stopTrackAndResetDuration() {
    // reset
    this.stopTrack();
    this.duration = 0;
  }
  stopTrack() {
    this.mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
  }
  recordingTimeDisplay() {
    return this.convertToReadableTime(this.recordingTime());
  }
  recordingTime() {
    return this.isRecording() ? (Date.now() - this.startTime) * 0.001 : this.endDuration;
  }
  isRecording() {
    return this.mediaRecorder ? this.mediaRecorder.state === "recording" : false;
  }
  convertToReadableTime(timeInSec) {
    const sec = timeInSec | 0;
    return "" + this.minSecStr((sec / 60) | 0) + ":" + this.minSecStr(sec % 60);
  }
  minSecStr(n) {
    return (n < 10 ? "0" : "") + n;
  }
}
